import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

interface AdminUser {
  id: number;
  username: string;
  name: string;
  email: string | null;
  profilePic: string | null;
  provider: string | null;
  isAdmin: boolean;
  isBlocked: boolean;
  blockedAt: string | null;
  blockReason: string | null;
  createdAt: string;
  deletedAt: string | null;
  _count: {
    posts: number;
    comments: number;
    reportsReceived: number;
    reportsCreated: number;
    followers: number;
    followings: number;
  };
}

const providerLabels: Record<string, string> = {
  email: '이메일',
  kakao: '카카오',
  naver: '네이버',
  apple: '애플',
  google: 'Google',
};

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>(''); // '', 'blocked', 'admin', 'active'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken');
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (providerFilter) params.provider = providerFilter;
      if (statusFilter === 'blocked') params.isBlocked = true;
      else if (statusFilter === 'admin') params.isAdmin = true;
      else if (statusFilter === 'active') params.isBlocked = false;

      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.items);
      setTotal(response.data.meta.total);
    } catch (err: any) {
      setError(err.response?.data?.message || '회원 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, providerFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">회원 관리</Typography>
        <Box sx={{ ml: 'auto' }}>
          <IconButton onClick={fetchUsers}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="검색 (username/name/email)"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            fullWidth={isMobile}
          >
            검색
          </Button>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={statusFilter}
              label="상태"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="active">활성</MenuItem>
              <MenuItem value="blocked">차단</MenuItem>
              <MenuItem value="admin">관리자</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
            <InputLabel>가입경로</InputLabel>
            <Select
              value={providerFilter}
              label="가입경로"
              onChange={(e) => {
                setProviderFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="email">이메일</MenuItem>
              <MenuItem value="kakao">카카오</MenuItem>
              <MenuItem value="naver">네이버</MenuItem>
              <MenuItem value="apple">애플</MenuItem>
              <MenuItem value="google">Google</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">조회된 회원이 없습니다.</Typography>
            </Paper>
          ) : (
            users.map((u) => (
              <Card key={u.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/users/${u.id}`)}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5}>
                    <Avatar src={u.profilePic ?? undefined} sx={{ width: 72, height: 72, flexShrink: 0 }}>
                      {u.name?.[0]}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        #{u.id} · @{u.username}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {u.deletedAt ? (
                          <Chip label="탈퇴" size="small" sx={{ height: 20, fontSize: 10 }} />
                        ) : u.isBlocked ? (
                          <Chip label="차단" size="small" color="error" sx={{ height: 20, fontSize: 10 }} />
                        ) : u.isAdmin ? (
                          <Chip label="관리자" size="small" color="primary" sx={{ height: 20, fontSize: 10 }} />
                        ) : (
                          <Chip label="활성" size="small" color="success" sx={{ height: 20, fontSize: 10 }} />
                        )}
                        <Chip
                          label={providerLabels[u.provider ?? 'email'] ?? u.provider ?? '-'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(u.createdAt).toLocaleDateString('ko-KR')}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">
                          글 <b>{u._count.posts}</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          댓 <b>{u._count.comments}</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          팔 <b>{u._count.followers}</b>
                        </Typography>
                        <Typography
                          variant="caption"
                          color={u._count.reportsReceived > 0 ? 'warning.main' : 'text.secondary'}
                        >
                          신 <b>{u._count.reportsReceived}</b>
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="column" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => navigate(`/users/${u.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>회원</TableCell>
              <TableCell>가입경로</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="right">게시물</TableCell>
              <TableCell align="right">댓글</TableCell>
              <TableCell align="right">받은 신고</TableCell>
              <TableCell>가입일</TableCell>
              <TableCell align="center">상세</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                  조회된 회원이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={u.profilePic ?? undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {u.name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {u.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{u.username}
                          {u.email && ` · ${u.email}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {providerLabels[u.provider ?? 'email'] ?? u.provider ?? '-'}
                  </TableCell>
                  <TableCell>
                    {u.deletedAt ? (
                      <Chip label="탈퇴" size="small" color="default" />
                    ) : u.isBlocked ? (
                      <Chip label="차단" size="small" color="error" />
                    ) : u.isAdmin ? (
                      <Chip label="관리자" size="small" color="primary" />
                    ) : (
                      <Chip label="활성" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">{u._count.posts}</TableCell>
                  <TableCell align="right">{u._count.comments}</TableCell>
                  <TableCell align="right">
                    {u._count.reportsReceived > 0 ? (
                      <Chip
                        label={u._count.reportsReceived}
                        color="warning"
                        size="small"
                      />
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(u.createdAt).toLocaleDateString('ko-KR')}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/users/${u.id}`)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
      )}
    </Box>
  );
};

export default UserList;
